import { create } from 'zustand';
import { api, ApiError, type AuthUser } from '@/lib/api';
import { clearToken, getToken, setToken } from '@/lib/session';

interface AuthState {
  user: AuthUser | null;
  /**
   * True until we know whether there IS a session.
   *
   * Distinct from `user === null`. On a hard refresh we have a token in
   * localStorage but have not yet confirmed it is still valid — for that moment
   * the user is neither known-in nor known-out. Routing on `!user` during that
   * gap would bounce a signed-in user to the login page on every refresh.
   */
  initializing: boolean;

  initialize: () => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  initializing: true,

  /**
   * Rehydrates the session from a stored token. If there is no token, or the
   * server rejects it, we land signed-out. Returns a no-op cleanup so callers
   * can keep treating this like a subscription.
   */
  initialize: () => {
    const token = getToken();
    if (!token) {
      set({ user: null, initializing: false });
      return () => {};
    }

    void api.auth
      .me()
      .then((user) => set({ user, initializing: false }))
      .catch(() => {
        // Token invalid/expired — request() already cleared it on the 401.
        clearToken();
        set({ user: null, initializing: false });
      });

    return () => {};
  },

  signIn: async (email, password) => {
    try {
      const { token, user } = await api.auth.login(email, password);
      setToken(token);
      set({ user });
    } catch (error) {
      throw new Error(friendlyAuthError(error));
    }
  },

  signUp: async (email, password) => {
    try {
      const { token, user } = await api.auth.register(email, password);
      setToken(token);
      set({ user });
    } catch (error) {
      throw new Error(friendlyAuthError(error));
    }
  },

  signOut: async () => {
    clearToken();
    set({ user: null });
  },
}));

/** Turns API errors into copy a person can act on. */
function friendlyAuthError(error: unknown): string {
  const message = error instanceof ApiError ? error.message : 'Something went wrong. Try again.';
  const lower = message.toLowerCase();

  if (lower.includes('already exists')) {
    return 'An account with that email already exists. Try signing in.';
  }
  if (lower.includes('combination is not right') || lower.includes('invalid')) {
    return 'That email and password combination is not right.';
  }
  if (lower.includes('at least 6')) {
    return 'Your password needs to be at least 6 characters.';
  }
  if (lower.includes('too many') || lower.includes('rate')) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  return message;
}
